import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VideoPanel = ({ 
  isCallActive, 
  isMuted, 
  isCameraOff, 
  isScreenSharing,
  onToggleMute,
  onToggleCamera,
  onToggleScreenShare,
  onEndCall,
  patientName,
  callDuration 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [providerVideoPosition, setProviderVideoPosition] = useState({ x: 20, y: 20 });
  const providerVideoRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleMouseDown = (e) => {
    if (!providerVideoRef?.current) return;
    
    const rect = providerVideoRef?.current?.getBoundingClientRect();
    dragOffset.current = {
      x: e?.clientX - rect?.left,
      y: e?.clientY - rect?.top
    };
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const container = e?.currentTarget?.getBoundingClientRect();
    const newX = Math.max(0, Math.min(container?.width - 200, e?.clientX - container?.left - dragOffset?.current?.x));
    const newY = Math.max(0, Math.min(container?.height - 150, e?.clientY - container?.top - dragOffset?.current?.y));
    
    setProviderVideoPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden h-full">
      {/* Main Patient Video */}
      <div className="w-full h-full relative">
        {isCallActive ? (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-32 h-32 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="User" size={48} color="white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{patientName}</h3>
              <div className="flex items-center justify-center space-x-2 text-sm opacity-75">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Connected • {formatDuration(callDuration)}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <div className="text-center text-white">
              <Icon name="VideoOff" size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg opacity-75">Waiting for patient to join...</p>
            </div>
          </div>
        )}

        {/* Call Status Overlay */}
        <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
          <div className="flex items-center space-x-2 text-white text-sm">
            <div className={`w-2 h-2 rounded-full ${isCallActive ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span>{isCallActive ? 'Connected' : 'Connecting...'}</span>
          </div>
        </div>

        {/* Provider Video Overlay */}
        {isCallActive && (
          <div
            ref={providerVideoRef}
            className="absolute w-48 h-36 bg-gray-700 rounded-lg border-2 border-white/20 cursor-move shadow-lg"
            style={{
              left: `${providerVideoPosition?.x}px`,
              top: `${providerVideoPosition?.y}px`,
              zIndex: 10
            }}
            onMouseDown={handleMouseDown}
          >
            <div className="w-full h-full bg-gray-700 rounded-lg flex items-center justify-center relative overflow-hidden">
              {isCameraOff ? (
                <div className="text-center text-white">
                  <Icon name="VideoOff" size={24} className="mx-auto mb-2 opacity-75" />
                  <p className="text-xs opacity-75">Camera Off</p>
                </div>
              ) : (
                <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="white" />
                  </div>
                </div>
              )}
              
              {/* Provider Video Controls */}
              <div className="absolute bottom-1 right-1 flex space-x-1">
                {isMuted && (
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <Icon name="MicOff" size={12} color="white" />
                  </div>
                )}
              </div>
              
              {/* Drag Indicator */}
              <div className="absolute top-1 left-1 text-white/50">
                <Icon name="Move" size={12} />
              </div>
            </div>
          </div>
        )}

        {/* Screen Share Indicator */}
        {isScreenSharing && (
          <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-2 rounded-lg text-sm flex items-center space-x-2">
            <Icon name="Monitor" size={16} />
            <span>Screen Sharing</span>
          </div>
        )}
      </div>
      {/* Video Controls */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
        <div className="flex items-center space-x-4 bg-black/50 backdrop-blur-sm rounded-full px-6 py-3">
          <Button
            variant={isMuted ? "destructive" : "secondary"}
            size="icon"
            onClick={onToggleMute}
            className="w-12 h-12 rounded-full"
          >
            <Icon name={isMuted ? "MicOff" : "Mic"} size={20} />
          </Button>

          <Button
            variant={isCameraOff ? "destructive" : "secondary"}
            size="icon"
            onClick={onToggleCamera}
            className="w-12 h-12 rounded-full"
          >
            <Icon name={isCameraOff ? "VideoOff" : "Video"} size={20} />
          </Button>

          <Button
            variant={isScreenSharing ? "default" : "secondary"}
            size="icon"
            onClick={onToggleScreenShare}
            className="w-12 h-12 rounded-full"
          >
            <Icon name="Monitor" size={20} />
          </Button>

          <Button
            variant="destructive"
            size="icon"
            onClick={onEndCall}
            className="w-12 h-12 rounded-full"
          >
            <Icon name="PhoneOff" size={20} />
          </Button>
        </div>
      </div>
      {/* Emergency Alert Button */}
      <div className="absolute top-4 right-4">
        <Button
          variant="destructive"
          size="sm"
          iconName="AlertTriangle"
          iconPosition="left"
          className="bg-red-600 hover:bg-red-700"
        >
          Emergency
        </Button>
      </div>
    </div>
  );
};

export default VideoPanel;